import { useCallback, useEffect, useState, useRef } from "react";
import { getSectionStylesURL } from "../../utils/ConstantsURL";
import { unwrapApiResponse } from "../../utils/apiResponse";

export const useGetSectionStyles = (token, documentId) => {
  const [sections, setSections] = useState([]);
  const [stylesBySectionId, setStylesBySectionId] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const resolveRefreshRef = useRef(null);

  const refresh = useCallback(() => {
    return new Promise((resolve) => {
      resolveRefreshRef.current = resolve;
      setRefreshIndex((p) => p + 1);
    });
  }, []);

  useEffect(() => {
    if (!token || !documentId) return;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(getSectionStylesURL(documentId), {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const list = await unwrapApiResponse(res);
        const items = Array.isArray(list) ? list : [];

        setSections(items.map((d) => ({ id: d.sectionId, type: d.type })));

        const map = {};
        for (const item of items) {
          map[item.sectionId] = item.stylesEs || {};
        }
        setStylesBySectionId(map);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("useGetSectionStyles:", e);
          setError(e.message || "Error al obtener estilos de secciones");
        }
      } finally {
        setLoading(false);
        if (resolveRefreshRef.current) {
          resolveRefreshRef.current();
          resolveRefreshRef.current = null;
        }
      }
    };

    load();
    return () => controller.abort();
  }, [token, documentId, refreshIndex]);

  const updateLocalSectionStyle = useCallback((sectionId, attributeName, updates) => {
    setStylesBySectionId((prev) => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [attributeName]: {
          ...(prev[sectionId]?.[attributeName] || {}),
          ...updates,
        },
      },
    }));
  }, []);

  return {
    sections,
    stylesBySectionId,
    setStylesBySectionId,
    loading,
    error,
    refresh,
    updateLocalSectionStyle,
  };
};

export default useGetSectionStyles;
